import { useEffect, useRef } from "react";
import type { Config, Data, Layout } from "plotly.js";
import PlotlyImport from "plotly.js-dist-min";

type PlotlyApi = {
  newPlot: (
    root: HTMLElement,
    data: Data[],
    layout?: Partial<Layout>,
    config?: Partial<Config>,
  ) => Promise<void>;
  react: (
    root: HTMLElement,
    data: Data[],
    layout?: Partial<Layout>,
    config?: Partial<Config>,
  ) => Promise<void>;
  relayout: (root: HTMLElement, update: Record<string, unknown>) => Promise<void>;
  purge: (root: HTMLElement) => void;
  Plots?: { resize: (root: HTMLElement) => void };
};

function plotly(): PlotlyApi {
  const mod = PlotlyImport as unknown as { default?: PlotlyApi };
  return (mod.default ?? mod) as PlotlyApi;
}

type PlotlyDiv = HTMLDivElement & {
  on?: (event: string, handler: (data: Record<string, unknown>) => void) => void;
  removeListener?: (event: string, handler: (data: Record<string, unknown>) => void) => void;
};

type PlotlyChartProps = {
  data: Data[];
  layout: Partial<Layout>;
  config?: Partial<Config>;
  className?: string;
  /** Bumps when live data changes — ensures Plotly.react runs. */
  revision?: string | number;
  /** Fired on box zoom / pan / double-click reset. */
  onRelayout?: (event: Record<string, unknown>) => void;
};

export function PlotlyChart({
  data,
  layout,
  config,
  className = "h-full w-full",
  revision,
  onRelayout,
}: PlotlyChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const ready = useRef(false);
  const dataRef = useRef(data);
  const layoutRef = useRef(layout);
  const configRef = useRef(config);
  const onRelayoutRef = useRef(onRelayout);
  const relayoutHandlerRef = useRef((ev: Record<string, unknown>) => {
    onRelayoutRef.current?.(ev);
  });
  dataRef.current = data;
  layoutRef.current = layout;
  configRef.current = config;
  onRelayoutRef.current = onRelayout;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const api = plotly();
    const fullLayout = { autosize: true, ...layoutRef.current };
    const fullConfig = { responsive: true, displayModeBar: false, ...configRef.current };

    const attachRelayout = () => {
      if (!onRelayoutRef.current) return;
      const gd = el as PlotlyDiv;
      const handler = relayoutHandlerRef.current;
      gd.removeListener?.("plotly_relayout", handler);
      gd.on?.("plotly_relayout", handler);
    };

    const enforceAxisRanges = () => {
      const layout = layoutRef.current;
      const update: Record<string, unknown> = {};
      const yRange = layout.yaxis?.range;
      if (
        layout.yaxis?.autorange === false &&
        Array.isArray(yRange) &&
        yRange.length === 2 &&
        yRange.every((v) => typeof v === "number" && Number.isFinite(v))
      ) {
        update["yaxis.autorange"] = false;
        update["yaxis.range"] = yRange;
      }
      const xRange = layout.xaxis?.range;
      if (
        layout.xaxis?.autorange === false &&
        Array.isArray(xRange) &&
        xRange.length === 2
      ) {
        update["xaxis.autorange"] = false;
        update["xaxis.range"] = xRange;
      }
      if (Object.keys(update).length > 0) {
        void api.relayout(el, update);
      }
    };

    const afterPlot = () => {
      attachRelayout();
      enforceAxisRanges();
    };

    if (!ready.current) {
      ready.current = true;
      void api.newPlot(el, dataRef.current, fullLayout, fullConfig).then(afterPlot);
    } else {
      void api.react(el, dataRef.current, fullLayout, fullConfig).then(afterPlot);
    }
    // Only redraw when revision bumps — avoids full Plotly.react on unrelated parent re-renders.
  }, [revision]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const api = plotly();
    const onResize = () => {
      if (ready.current) api.Plots?.resize(el);
    };
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      api.purge(el);
      ready.current = false;
    };
  }, []);

  return <div ref={ref} className={className} />;
}
